import { Percentage } from '@ui/components/Amount/Percentage';
import { Copy, ShareLink, X as XIcon } from '@ui/components/icons';
import {
  ShareIdentityControls,
  ShareIdentityProfile,
  useShareIdentity,
} from '@ui/components/share/useShareIdentity';
import { basicToaster } from '@ui/components/Toast';
import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { useIsConnectedOnload } from '@ui/features/Wallet/hooks/useIsConnectedOnload';
import { usePriceLive } from '@ui/hooks/price/usePriceLive';
import { useTradePairInfosByPairBase } from '@ui/hooks/trade/useTradePairInfos';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { Textarea } from '@ui/lib/park/ui/textarea';
import { useGenesisAirdropUIData } from '@ui/services/contract/hooks/GenesisAirdrop/useData';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import {
  canShareNodeImage,
  copyNodeImageToClipboard,
  downloadNodeAsPng,
  shareNodeImage,
} from '@ui/utils/image/exportNodeAsImage';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flex, Grid, HStack, Stack, styled, VStack } from 'styled-system/jsx';
import { useAccount } from 'wagmi';

import {
  ShareInviteCodeToggle,
  useShareReferralCode,
} from '../ShareInviteCodeToggle';
import { usePositionListContext } from './context';
import { PositionMarketData } from './types';

const PEPE_IMAGES = [
  '/images/share-pepe/ultra-pepe-1.svg',
  '/images/share-pepe/ultra-pepe-2.svg',
  '/images/share-pepe/ultra-pepe-3.svg',
  '/images/share-pepe/ultra-pepe-4.svg',
];

// The share card is authored at a fixed size and scaled to fit the modal
// width, so proportions (and the exported image) are identical on every device.
const SHARE_CARD_WIDTH = 500;
const SHARE_CARD_HEIGHT = 260;

type PositionShareOverlayProps = {
  position: Position;
  children?: React.ReactNode;
  marketData?: PositionMarketData;
};

export const PositionShareOverlay = memo(
  (props: PositionShareOverlayProps) => {
    return <PositionShareOverlayShell {...props} />;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.position.id === nextProps.position.id &&
      prevProps.marketData?.pairMarketPrice ===
        nextProps.marketData?.pairMarketPrice &&
      prevProps.marketData?.collateralMarketPrice ===
        nextProps.marketData?.collateralMarketPrice &&
      prevProps.marketData?.tradePair === nextProps.marketData?.tradePair
    );
  },
);

const PositionShareOverlayShell = ({
  position,
  children,
  marketData,
}: PositionShareOverlayProps) => {
  const [open, setOpen] = useState(false);
  const [requestedOpen, setRequestedOpen] = useState(false);
  const [shouldMountContent, setShouldMountContent] = useState(false);

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(details) => {
        if (details.open) {
          setShouldMountContent(true);
          if (children) {
            setOpen(true);
          } else {
            setRequestedOpen(true);
          }

          return;
        }

        setOpen(false);
        setRequestedOpen(false);
      }}
    >
      {children ? (
        <Dialog.Trigger asChild>{children}</Dialog.Trigger>
      ) : (
        <Button
          variant="ghost"
          p={0}
          w="28px"
          h="28px"
          minW="28px"
          onClick={() => {
            setShouldMountContent(true);
            setRequestedOpen(true);
          }}
          loading={requestedOpen && !open}
          disabled={requestedOpen && !open}
        >
          <Icon
            boxSize="18px"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <styled.img src="/images/share.svg" width="100%" height="100%" />
          </Icon>
        </Button>
      )}

      {shouldMountContent && (
        <PositionShareOverlayContent
          position={position}
          marketData={marketData}
          open={open}
          requestedOpen={requestedOpen}
          setOpen={setOpen}
          setRequestedOpen={setRequestedOpen}
        />
      )}
    </Dialog.Root>
  );
};

const PositionShareOverlayContent = (
  props: PositionShareOverlayProps & {
    open: boolean;
    requestedOpen: boolean;
    setOpen: (open: boolean) => void;
    setRequestedOpen: (requestedOpen: boolean) => void;
  },
) => {
  if (props.marketData) {
    return (
      <PositionShareOverlayContentWithData
        {...props}
        marketData={props.marketData}
      />
    );
  }

  return <PositionShareOverlayContentWithHooks {...props} />;
};

const PositionShareOverlayContentWithHooks = ({
  position,
  ...props
}: PositionShareOverlayProps & {
  open: boolean;
  requestedOpen: boolean;
  setOpen: (open: boolean) => void;
  setRequestedOpen: (requestedOpen: boolean) => void;
}) => {
  const pairMarketPrice = usePriceLive(position.pair.baseSymbol);
  const tradePair = useTradePairInfosByPairBase(position.pair.address);
  const collateralMarketPrice = usePayTokenPriceLatest(
    position.collateralToken,
  );

  return (
    <PositionShareOverlayContent
      position={position}
      {...props}
      marketData={{
        pairMarketPrice,
        collateralMarketPrice,
        tradePair,
      }}
    />
  );
};

const PositionShareOverlayContentWithData = memo(
  ({
    position,
    marketData,
    open,
    requestedOpen,
    setOpen,
    setRequestedOpen,
  }: PositionShareOverlayProps & {
    marketData: PositionMarketData;
    open: boolean;
    requestedOpen: boolean;
    setOpen: (open: boolean) => void;
    setRequestedOpen: (requestedOpen: boolean) => void;
  }) => {
    const [shareText, setShareText] = useState(
      'Trade $MON perps seamlessly on @LeverUp_xyz',
    );
    const [copied, setCopied] = useState(false);
    const [showCode, setShowCode] = useState(true);
    // Mobile/in-app browsers can't reliably copy images to the clipboard, but
    // they can open the native share sheet (Save to Photos + share anywhere).
    const [canShare, setCanShare] = useState(false);
    useEffect(() => {
      setCanShare(canShareNodeImage());
    }, []);
    // Disable the image button while rendering so a half-loaded card can't be
    // saved/copied (image generation waits for all images to finish loading).
    const [imageBusy, setImageBusy] = useState(false);
    const imageBusyRef = useRef(false);

    const shareRef = useRef<HTMLDivElement | null>(null);
    const scalerWrapRef = useRef<HTMLDivElement | null>(null);
    const roRef = useRef<ResizeObserver | null>(null);
    const [cardScale, setCardScale] = useState(1);
    const identity = useShareIdentity();

    const priceDisplayDecimals = useTokenDisplayDecimals(
      position.pair.baseSymbol,
      position.pair.priceDisplayDecimals,
    );
    const pairMarketPrice = marketData.pairMarketPrice;
    const tradePair = marketData.tradePair;
    const collateralMarketPrice = marketData.collateralMarketPrice;
    const { feeConfig } = usePositionListContext();
    const { address } = useAccount();
    const {
      data: _airdropData,
      isLoading: isAirdropLoading,
      isFetching: isAirdropFetching,
    } = useGenesisAirdropUIData(address);
    const isConnectedOnload = useIsConnectedOnload();
    const { referralCode, isLoading: isReferralCodeLoading } =
      useShareReferralCode(address);

    const pnlPercentage = useMemo(() => {
      if (
        !feeConfig ||
        !pairMarketPrice ||
        !collateralMarketPrice ||
        !tradePair
      )
        return undefined;

      return position
        .getPnlAfterFeePercentage({
          ...feeConfig,
          pairMarketPrice,
          collateralMarketPrice,
          tradePair,
        })
        .toNumber();
    }, [
      collateralMarketPrice,
      feeConfig,
      pairMarketPrice,
      position,
      tradePair,
    ]);

    // Only open modal when wallet connection status is resolved
    // and (if connected) airdrop data has been fetched to avoid UI flicker.
    const isConnectionResolved = isConnectedOnload !== null;
    const shouldWaitForAirdrop = isConnectedOnload === true;
    const isAirdropReady =
      !shouldWaitForAirdrop || !(isAirdropLoading || isAirdropFetching);
    const canOpen = isConnectionResolved && isAirdropReady;

    useEffect(() => {
      if (requestedOpen && canOpen) {
        setOpen(true);
        setRequestedOpen(false);
      }
    }, [canOpen, requestedOpen, setOpen, setRequestedOpen]);

    // Scale the fixed-size share card down to fit the modal width.
    const measureCardScale = useCallback(() => {
      const el = scalerWrapRef.current;
      if (!el) return;
      const width = el.clientWidth || el.getBoundingClientRect().width;
      if (width > 0) setCardScale(Math.min(1, width / SHARE_CARD_WIDTH));
    }, []);

    // Callback ref so the ResizeObserver attaches the moment the node mounts.
    // The dialog content is portaled/lazy-mounted, so it appears in a later
    // commit than an [open] effect would fire — a plain ref check bails early
    // and the card stays stuck at scale 1 (overflowing narrow screens).
    const setScalerWrapRef = useCallback(
      (node: HTMLDivElement | null) => {
        scalerWrapRef.current = node;
        roRef.current?.disconnect();
        roRef.current = null;
        if (!node) return;
        measureCardScale();
        requestAnimationFrame(measureCardScale);
        const ro = new ResizeObserver(measureCardScale);
        ro.observe(node);
        roRef.current = ro;
      },
      [measureCardScale],
    );

    useEffect(() => {
      window.addEventListener('resize', measureCardScale);

      return () => window.removeEventListener('resize', measureCardScale);
    }, [measureCardScale]);

    // no-op
    const shareBgImage =
      pnlPercentage !== undefined && pnlPercentage < 0
        ? '/images/share-down.svg'
        : '/images/share-up.svg';

    const initialPerpeIdx = useMemo(() => {
      if (pnlPercentage === undefined) return 0;
      const isPositive = pnlPercentage > 0;

      return isPositive ? 0 : 3;
    }, [pnlPercentage]);

    const [selectedPerpeIdx, setSelectedPerpeIdx] = useState<number | null>(
      initialPerpeIdx,
    );

    const onCopyImage = async () => {
      const node = shareRef.current;
      if (!node || imageBusyRef.current) return;
      imageBusyRef.current = true;
      setImageBusy(true);
      try {
        // Mobile: open the native share sheet (Save to Photos + share
        // anywhere). Clipboard image writes are unreliable on iOS/in-app
        // browsers.
        if (canShare) {
          try {
            await shareNodeImage(node, {
              pixelRatio: 3,
              fileName: 'leverup-share.png',
            });

            return;
          } catch (e) {
            // User dismissed the share sheet — not an error.
            if ((e as { name?: string })?.name === 'AbortError') return;
            // Otherwise fall through to download.
            try {
              await downloadNodeAsPng(node, 'leverup-share.png', {
                pixelRatio: 3,
              });

              return;
            } catch {}
          }
        }

        // Desktop: copy to clipboard.
        try {
          await copyNodeImageToClipboard(node, { pixelRatio: 3 });
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch (e) {
          try {
            basicToaster.error({
              title: 'Copy failed, Please try again',
            });
          } catch {}
        }
      } finally {
        imageBusyRef.current = false;
        setImageBusy(false);
      }
    };

    const onShareX = async () => {
      if (!shareRef.current) return;
      try {
        await copyNodeImageToClipboard(shareRef.current, { pixelRatio: 3 });
      } catch (e) {
        // ignore copy failure; allow user to still tweet without image
      }
      const url = `https://x.com/intent/tweet?text=${encodeURIComponent(
        shareText,
      )}`;
      window.open(url, '_blank');
    };

    const onCopyLink = async () => {
      try {
        const origin = window.location.origin;
        const urlToCopy =
          showCode && referralCode
            ? `${origin}?code=${encodeURIComponent(referralCode)}`
            : window.location.href;
        await navigator.clipboard.writeText(urlToCopy);
        basicToaster.success({ title: 'Copied to clipboard' });
      } catch {}
    };

    if (!open) {
      return null;
    }

    return (
      <>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content
            bg="v2.bg.panel"
            color="greyWhite.lightGrey1"
            display="flex"
            flexDir="column"
            // Cap to the viewport and let the description scroll internally.
            // iOS Safari can't reliably scroll the fixed-position positioner.
            minH={{ base: '0', tablet: 'unset' }}
            maxH={{ base: '100dvh', tablet: '90dvh' }}
            overflow="hidden"
            minW={{
              base: 'full',
              tablet: '500px',
            }}
            maxW={{
              base: 'full',
              tablet: '500px',
            }}
            borderRadius="12px"
          >
            <Dialog.Title asChild p={0}>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                px="16px"
                py="12px"
                bg="v2.bg.panel"
                borderRadius="12px"
              >
                <Text textStyle="headings.large">Share Trade</Text>
                <Dialog.CloseTrigger boxShadow="none" bg="none">
                  <Icon w="20px" h="20px">
                    <XIcon />
                  </Icon>
                </Dialog.CloseTrigger>
              </Flex>
            </Dialog.Title>

            <Dialog.Description
              pt={0}
              display="flex"
              flexDir="column"
              gap={3}
              cursor="default"
              flex="1"
              minH="0"
              overflowY="auto"
              css={{
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                // Keep children at their natural height so the column overflows
                // and scrolls, instead of flexbox squishing them (which clipped
                // the card and prevented scrolling on iOS).
                '& > *': { flexShrink: 0 },
              }}
            >
              <styled.div
                ref={setScalerWrapRef}
                position="relative"
                className="group"
                w="full"
                overflow="hidden"
                // Hover border via outline (offset inward) so it doesn't eat
                // into the content box and clip the scaled card's bottom edge.
                outline="1px solid"
                outlineColor="transparent"
                outlineOffset="-1px"
                transition="all 0.15s ease"
                cursor="pointer"
                _hover={{
                  outlineColor: 'greyWhite.outline',
                  filter: 'brightness(1.03)',
                }}
                onClick={onCopyImage}
                style={{ height: SHARE_CARD_HEIGHT * cardScale }}
              >
                <styled.div
                  style={{
                    width: `${SHARE_CARD_WIDTH}px`,
                    height: `${SHARE_CARD_HEIGHT}px`,
                    transform: `scale(${cardScale})`,
                    transformOrigin: 'top left',
                  }}
                >
                  <styled.div
                    ref={shareRef}
                    position="relative"
                    width="500px"
                    height="260px"
                    overflow="hidden"
                    bgGradient="to-br"
                    gradientFrom="#0b0b0b"
                    gradientTo="#1f1f1f"
                  >
                    <styled.img
                      src={shareBgImage}
                      position="absolute"
                      top={0}
                      left={0}
                      width="100%"
                      height="100%"
                      objectFit="cover"
                      pointerEvents="none"
                    />
                    <Flex
                      position="absolute"
                      top="14px"
                      right="16px"
                      alignItems="center"
                      gap={2}
                    >
                      <styled.img
                        src="/images/logo.svg"
                        alt="LeverUp"
                        width="130px"
                        height="40px"
                      />
                    </Flex>
                    <VStack
                      position="absolute"
                      top="16px"
                      left="24px"
                      gap={2}
                      alignItems="flex-start"
                    >
                      <HStack gap={2}>
                        <styled.img
                          src={position.pair.image}
                          width="24px"
                          height="24px"
                        />
                        <Text
                          textStyle="headings.default"
                          color="greyWhite.lightGrey1"
                        >
                          {position.pair.baseName}
                        </Text>
                        <styled.span
                          px={2}
                          py={1}
                          borderRadius="4px"
                          bg={
                            position.isLong
                              ? 'price.priceUpBg'
                              : 'price.priceDownBg'
                          }
                          color={
                            position.isLong
                              ? 'price.priceUp'
                              : 'price.priceDown'
                          }
                          textStyle="text.small"
                        >
                          {position.isLong ? 'Long' : 'Short'}{' '}
                          {collateralMarketPrice
                            ? position
                                .getLeverage({
                                  collateralTokenMarketPrice:
                                    collateralMarketPrice,
                                })
                                .toFixed(1)
                            : '--'}
                          x
                        </styled.span>
                        <styled.span
                          px={2}
                          py={1}
                          borderRadius="4px"
                          bg="greyWhite.darkGary1"
                          color="greyWhite.lightGrey1"
                          textStyle="text.small"
                        >
                          Open
                        </styled.span>
                      </HStack>
                    </VStack>

                    <VStack
                      position="absolute"
                      left="24px"
                      top="72px"
                      alignItems="flex-start"
                    >
                      <Text
                        textStyle="numbers.medium"
                        fontSize="72px"
                        color="primary.orange"
                        whiteSpace="nowrap"
                      >
                        {pnlPercentage !== undefined && (
                          <Percentage
                            whiteSpace="nowrap"
                            textStyle="numbers.huge"
                            value={pnlPercentage}
                            textDecoration="none"
                            css={{ textDecoration: 'none' }}
                            opts={{
                              isShowSign: true,
                            }}
                            data-cy="PositionShareOverlay-PnlPercentage"
                            data-cy-value={pnlPercentage}
                          />
                        )}
                      </Text>
                    </VStack>

                    <ShareIdentityProfile
                      identity={identity}
                      code={showCode ? referralCode : undefined}
                      position="absolute"
                      left="24px"
                      top="152px"
                    />
                    <VStack
                      position="absolute"
                      left="24px"
                      bottom="18px"
                      gap={1}
                      alignItems="flex-start"
                    >
                      <HStack gap={6}>
                        <VStack alignItems="flex-start" gap={0}>
                          <Text
                            textStyle="text.xsmall"
                            color="greyWhite.lightGrey4"
                            whiteSpace="nowrap"
                          >
                            Entry Price
                          </Text>
                          <Text textStyle="numbers.default" whiteSpace="nowrap">
                            {position.entryPrice.toFormat(priceDisplayDecimals)}
                          </Text>
                        </VStack>
                        <VStack
                          alignItems="flex-start"
                          gap={0}
                          whiteSpace="nowrap"
                        >
                          <Text
                            textStyle="text.xsmall"
                            color="greyWhite.lightGrey4"
                          >
                            Market Price
                          </Text>
                          <Text textStyle="numbers.default" whiteSpace="nowrap">
                            {pairMarketPrice?.toFormat(priceDisplayDecimals) ??
                              '--'}
                          </Text>
                        </VStack>
                      </HStack>
                    </VStack>
                    {selectedPerpeIdx !== null && (
                      <styled.img
                        src={PEPE_IMAGES[selectedPerpeIdx]}
                        position="absolute"
                        right="24px"
                        bottom="0px"
                        width="180px"
                        height="180px"
                      />
                    )}
                    {/* Copied indicator (top-right inside the image) */}
                    <styled.div
                      data-ignore-capture="true"
                      position="absolute"
                      top="12px"
                      right="12px"
                      display={copied ? 'block' : 'none'}
                      bg="#19191e"
                      color="greyWhite.lightGrey1"
                      border="1px solid"
                      borderColor="greyWhite.outline"
                      px="10px"
                      py="6px"
                      borderRadius="8px"
                      textStyle="text.small"
                      pointerEvents="none"
                    >
                      Copied to clipboard
                    </styled.div>
                  </styled.div>
                </styled.div>

                {/* Hover Overlay */}
                <Flex
                  position="absolute"
                  top={0}
                  left={0}
                  w="full"
                  h="full"
                  alignItems="center"
                  justifyContent="center"
                  bg="rgba(0,0,0,0.3)"
                  opacity={0}
                  transition="opacity 0.2s"
                  _groupHover={{
                    opacity: {
                      base: 0,
                      md: 1,
                    },
                  }}
                  pointerEvents="none"
                >
                  <HStack
                    bg="rgba(0,0,0,0.7)"
                    px={3}
                    py={2}
                    borderRadius="8px"
                    gap={2}
                    backdropFilter="blur(4px)"
                  >
                    <Icon boxSize="16px" color="white">
                      <Copy />
                    </Icon>
                    <Text
                      textStyle="text.small"
                      color="white"
                      fontWeight="medium"
                    >
                      Click to Copy
                    </Text>
                  </HStack>
                </Flex>
              </styled.div>

              <ShareIdentityControls identity={identity} />

              <VStack px={6} alignItems="flex-start" gap={2}>
                <Text textStyle="headings.small" color="greyWhite.lightGrey3">
                  Customize your text
                </Text>
                <Textarea
                  value={shareText}
                  color="greyWhite.lightGrey3"
                  borderRadius="8px"
                  border="1px solid"
                  borderColor="greyWhite.outline"
                  bg="#19191e"
                  onChange={(e) => setShareText(e.target.value)}
                  placeholder="Add a caption"
                  rows={2}
                  outline="none"
                  boxShadow="none"
                  _focusVisible={{
                    borderColor: 'greyWhite.outline',
                    boxShadow: 'none',
                    outline: 'none',
                  }}
                />
              </VStack>

              <VStack px={6} alignItems="flex-start" gap={2}>
                <Text textStyle="headings.small" color="greyWhite.lightGrey3">
                  Add an UltraPepe
                </Text>
                <HStack gap={2} flexWrap="wrap" width="100%">
                  <styled.button
                    onClick={() => setSelectedPerpeIdx(null)}
                    width="18%"
                    height="60px"
                    borderRadius="8px"
                    bg="#19191e"
                    border="2px solid"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    cursor="pointer"
                    overflow="hidden"
                    borderColor={
                      selectedPerpeIdx === null
                        ? 'primary.orange'
                        : 'transparent'
                    }
                  >
                    <styled.img
                      src="/images/empty.svg"
                      width="30%"
                      height="30%"
                    />
                  </styled.button>
                  {PEPE_IMAGES.map((src, idx) => (
                    <styled.button
                      key={src}
                      onClick={() => setSelectedPerpeIdx(idx)}
                      p={2}
                      bg="#19191e"
                      width="18%"
                      height="60px"
                      borderRadius="8px"
                      cursor="pointer"
                      overflow="hidden"
                      border="2px solid"
                      borderColor={
                        selectedPerpeIdx === idx
                          ? 'primary.orange'
                          : 'transparent'
                      }
                    >
                      <styled.img src={src} width="100%" height="100%" />
                    </styled.button>
                  ))}
                </HStack>
              </VStack>

              <ShareInviteCodeToggle
                px={6}
                checked={showCode}
                onCheckedChange={setShowCode}
                hasCode={!!referralCode}
                isLoading={isReferralCodeLoading}
              />

              <VStack px={6} alignItems="flex-start" gap={1}>
                <Text textStyle="text.small" color="greyWhite.lightGrey3">
                  {canShare
                    ? "Tip: Tap 'Save Image' to save the card, then attach it to your post."
                    : "Tip: 'Share on X' automatically copies the image. Just paste (Ctrl+V) in your tweet."}
                </Text>
              </VStack>
              <Stack
                p={5}
                pt={0}
                gap={3}
                direction={{ base: 'column', md: 'row' }}
              >
                <Button
                  onClick={onShareX}
                  variant="primary"
                  size="big"
                  w="full"
                >
                  <HStack gap={2}>
                    <styled.img
                      src="/images/x-black.svg"
                      width="18px"
                      height="18px"
                    />
                    <span>Share on X</span>
                  </HStack>
                </Button>

                <Button
                  onClick={onCopyImage}
                  loading={imageBusy}
                  disabled={imageBusy}
                  variant="secondary"
                  size="big"
                  w="full"
                  border="1px solid #CCFF0066"
                  color="#FFD600"
                  _hover={{
                    borderColor: '#FFD600',
                    color: '#FFD600',
                    bg: 'rgba(255,214,0,0.1)',
                  }}
                >
                  <HStack gap={2}>
                    <Icon boxSize="5" color="#FFD600">
                      <Copy />
                    </Icon>
                    <span>{canShare ? 'Save Image' : 'Copy Image'}</span>
                  </HStack>
                </Button>

                <Button
                  onClick={onCopyLink}
                  variant="secondary"
                  size="big"
                  w="full"
                  border="1px solid #CCFF0066"
                  color="#FFD600"
                  _hover={{
                    borderColor: '#FFD600',
                    color: '#FFD600',
                    bg: 'rgba(255,214,0,0.1)',
                  }}
                >
                  <HStack gap={2}>
                    <Icon boxSize="5" color="#FFD600">
                      <ShareLink />
                    </Icon>
                    <span>Copy Link</span>
                  </HStack>
                </Button>
              </Stack>
            </Dialog.Description>
          </Dialog.Content>
        </Dialog.Positioner>
      </>
    );
  },
);

PositionShareOverlay.displayName = 'PositionShareOverlay';
PositionShareOverlayShell.displayName = 'PositionShareOverlayShell';
PositionShareOverlayContentWithData.displayName =
  'PositionShareOverlayContentWithData';
